import { Injectable } from '@angular/core';
import { ApiService, IApiOption } from '@red/api';
import { PaginationAdapter } from '@red/data-access';
import { DcddAgentRemarkDto, EcddCustomerSignatureDto, EcddDto, EcddFileDto } from '@shared/data-access/dto';
import { EcddChecklistModel, ECDDMasteModel, EcddModel } from '@shared/data-access/models';
import { map, Observable } from 'rxjs';
import { IIngeniqueApiSearchAmlQuery, IIngeniqueApiSearchAmlResponse } from '@shared/components/ingenique-api';

@Injectable({
  providedIn: 'root',
})
export class EcddV2ApiService {
  static ROOT_POINT = 'transaction/ecdds-v2/portal';
  static CHECKLIST_POINT = 'transaction/checklists';
  static PUBLIC_ROOT_POINT = 'transaction/ecdds-v2/public';
  static DOWNLOAD_PDF_POINT = 'transaction/ecdds';
  static SEARCH_AML_POINT = 'transaction/ecdds/aml/search';
  static FOR_RESALE_POINT = 'transaction/ecdds/portal/for-resale';
  static RETRY_ATTACHMENTS_POINT = 'transaction/ecdds-v2/portal/sync-retry-attachment-error';
  static ROOT_POINT_REWORK = 'transaction/ecdds-v2/portal/rework';
  static SYNC_RETRY_POINT = 'transaction/ecdds-v2/portal/sync-retry';

  constructor(private _apiService: ApiService) { }

  get(itemId: number) {
    return this._apiService.get(EcddV2ApiService.ROOT_POINT + '/' + itemId).pipe(map(res => EcddModel.fromJson(res)));
  }

  search(query = {}, option?: IApiOption) {
    return this._apiService.get(EcddV2ApiService.ROOT_POINT, query, { ...option, removeEmpty: { enable: true } }).pipe(map(data => new PaginationAdapter(ECDDMasteModel, data)));
  }

  create(data: EcddDto) {
    return this._apiService.post(EcddV2ApiService.ROOT_POINT, data).pipe(map(res => EcddModel.fromJson(res)));
  }

  update(id: number, data: EcddDto) {
    return this._apiService.patch(EcddV2ApiService.ROOT_POINT + '/' + id, data, { excludeFields: [''] });
  }

  delete(id: number) {
    return this._apiService.delete(EcddV2ApiService.ROOT_POINT + '/' + id);
  }

  submit(id: number) {
    return this._apiService.post(EcddV2ApiService.ROOT_POINT + '/' + id + '/submit');
  }

  searchChecklist(query = {}, option?: IApiOption) {
    return this._apiService.get(EcddV2ApiService.CHECKLIST_POINT, query, option).pipe(map(data => new PaginationAdapter(EcddChecklistModel, data)));
  }

  getChecklist(id: number) {
    return this._apiService.get(EcddV2ApiService.CHECKLIST_POINT + '/' + id).pipe(map(res => EcddChecklistModel.fromJson(res)));
  }

  getPublic(id: number | string, query = {}) {
    return this._apiService.get(EcddV2ApiService.PUBLIC_ROOT_POINT + '/' + id, query).pipe(map(res => EcddModel.fromJson(res)));
  }

  updateCustomerSignature(id: number | string, data: EcddCustomerSignatureDto) {
    return this._apiService.post(EcddV2ApiService.PUBLIC_ROOT_POINT + '/' + id + '/customer-signature', data);
  }

  downloadPdf(id: number) {
    return this._apiService.get(
      EcddV2ApiService.DOWNLOAD_PDF_POINT + '/' + id + '/download-pdf',
      {},
      { pretreatmentResponse: false, requestOptions: { responseType: 'arraybuffer' } }
    );
  }

  downloadPublicPdf(id: number | string, query = {}) {
    return this._apiService.get(
      EcddV2ApiService.PUBLIC_ROOT_POINT + '/' + id + '/download-pdf',
      query,
      { pretreatmentResponse: false, requestOptions: { responseType: 'arraybuffer' } }
    );
  }

  searchAml(query: IIngeniqueApiSearchAmlQuery, option?: IApiOption): Observable<IIngeniqueApiSearchAmlResponse[]> {
    return this._apiService.post(EcddV2ApiService.SEARCH_AML_POINT, query, option);
  }

  searchForResale(query = {}, option?: IApiOption) {
    return this._apiService.get(EcddV2ApiService.FOR_RESALE_POINT, query, option).pipe(map(data => new PaginationAdapter(EcddModel, data)));
  }

  viewPdf(id: number, data: EcddFileDto): Observable<any> {
    const fileId = Number(data.fileId);
    return this._apiService.get(EcddV2ApiService.ROOT_POINT + '/' + id + '/download-pdf', { fileId });
  }

  getSignatureToken(id: number | string) {
    return this._apiService.get(EcddV2ApiService.ROOT_POINT + '/' + id + '/customer-signature-token');
  }

  regenerateAttachments(ecddCode: string) {
    return this._apiService.post(EcddV2ApiService.RETRY_ATTACHMENTS_POINT, { ecddCode });
  }

  rework(id: number) {
    return this._apiService.post(EcddV2ApiService.ROOT_POINT_REWORK + '/' + id);
  }

  refreshStatus(ecddCode: string) {
    return this._apiService.post(EcddV2ApiService.SYNC_RETRY_POINT, { ecddCode });
  }

  checkCanEditPropertyAddress(id: number | string) {
    return this._apiService.get(EcddV2ApiService.ROOT_POINT + '/availability/' + id);
  }

  updateAgentRemarks(id: number, data: DcddAgentRemarkDto): Observable<any> {
    return this._apiService.patch(EcddV2ApiService.ROOT_POINT + '/' + id + '/remarks', data);
  }

  viewQuickAmlReport(id: number, screeningId: number): Observable<any> {
    return this._apiService.get(EcddV2ApiService.ROOT_POINT + '/' + id + '/quick-aml-report', { screeningId });
  }


}
